// DialogRouter.tsx — Dispatches to the correct dialog component based on ui.activeDialog
// All dialogs use the shared .dialog-overlay / .dialog-box CSS classes

import { useStore } from '../store/useStore';
import ProjectSetupDialog    from '../dialogs/ProjectSetupDialog';
import ProjectLoadDialog     from '../dialogs/ProjectLoadDialog';
import ProjectSettingsDialog from '../dialogs/ProjectSettingsDialog';
import PanelOptionsDialog    from '../dialogs/PanelOptionsDialog';
import PanelDimsDialog       from '../dialogs/PanelDimsDialog';
import RoughOpeningsDialog   from '../dialogs/RoughOpeningsDialog';
import WindowsDialog         from '../dialogs/WindowsDialog';
import ManDoorsDialog        from '../dialogs/ManDoorsDialog';
import DockLevelersDialog    from '../dialogs/DockLevelersDialog';
import RectBlockoutsDialog   from '../dialogs/RectBlockoutsDialog';
import RoundBlockoutsDialog  from '../dialogs/RoundBlockoutsDialog';
import HorizFeaturesDialog   from '../dialogs/HorizFeaturesDialog';
import VertFeaturesDialog    from '../dialogs/VertFeaturesDialog';
import PilastersDialog       from '../dialogs/PilastersDialog';
import LedgersDialog         from '../dialogs/LedgersDialog';
import TopProfileDialog      from '../dialogs/TopProfileDialog';
import FootingShapesDialog   from '../dialogs/FootingShapesDialog';
import SideSlabsDialog       from '../dialogs/SideSlabsDialog';
import SlopeDetailsDialog    from '../dialogs/SlopeDetailsDialog';
import BracePointsDialog     from '../dialogs/BracePointsDialog';
import PickPointsDialog      from '../dialogs/PickPointsDialog';
import WeldConnectionsDialog from '../dialogs/WeldConnectionsDialog';
import ChangeThicknessDialog from '../dialogs/ChangeThicknessDialog';
import PanelAttributesDialog from '../dialogs/PanelAttributesDialog';
import SiteSetupDialog       from '../dialogs/SiteSetupDialog';
import SitePlaceDialog       from '../dialogs/SitePlaceDialog';
import DrawingSetupDialog    from '../dialogs/DrawingSetupDialog';
import RevisionNotesDialog   from '../dialogs/RevisionNotesDialog';
import SlopeCalcDialog       from '../dialogs/SlopeCalcDialog';
import MaterialsListDialog   from '../dialogs/MaterialsListDialog';
import BatchUtilitiesDialog  from '../dialogs/BatchUtilitiesDialog';
import ExportPanelDXF        from '../dialogs/ExportPanelDXF';
import ExportSiteDXF         from '../dialogs/ExportSiteDXF';
import PanelBookDialog       from '../dialogs/PanelBookDialog';
import AboutDialog           from '../dialogs/AboutDialog';
import ExportCVPDialog       from '../dialogs/ExportCVPDialog';
import ExportCVTDialog       from '../dialogs/ExportCVTDialog';
import ExportCVPanelDialog   from '../dialogs/ExportCVPanelDialog';
import ExportCVSiteDialog    from '../dialogs/ExportCVSiteDialog';
import ImportCVDialog        from '../dialogs/ImportCVDialog';
import ImportDXFDialog       from '../dialogs/ImportDXFDialog';

export default function DialogRouter() {
  const dialog = useStore(s => s.ui.activeDialog);
  const close  = useStore(s => s.closeDialog);

  function backdrop(e: React.MouseEvent) {
    if (e.target === e.currentTarget) close();
  }

  const map: Record<string, React.ReactNode> = {
    'project-setup':    <ProjectSetupDialog />,
    'project-load':     <ProjectLoadDialog />,
    'project-settings': <ProjectSettingsDialog />,
    'panel-options':    <PanelOptionsDialog />,
    'panel-dims':       <PanelDimsDialog />,
    'rough-openings':   <RoughOpeningsDialog />,
    'windows':          <WindowsDialog />,
    'man-doors':        <ManDoorsDialog />,
    'dock-levelers':    <DockLevelersDialog />,
    'rect-blockouts':   <RectBlockoutsDialog />,
    'round-blockouts':  <RoundBlockoutsDialog />,
    'horiz-features':   <HorizFeaturesDialog />,
    'vert-features':    <VertFeaturesDialog />,
    'pilasters':        <PilastersDialog />,
    'ledgers':          <LedgersDialog />,
    'top-profile':      <TopProfileDialog />,
    'footing-shapes':   <FootingShapesDialog />,
    'side-slabs':       <SideSlabsDialog />,
    'slope-details':    <SlopeDetailsDialog />,
    'brace-points':     <BracePointsDialog />,
    'pick-points':      <PickPointsDialog />,
    'weld-connections': <WeldConnectionsDialog />,
    'change-thickness': <ChangeThicknessDialog />,
    'panel-attributes': <PanelAttributesDialog />,
    'site-setup':       <SiteSetupDialog />,
    'site-place':       <SitePlaceDialog />,
    'site-edit':        <SitePlaceDialog />,
    'site-dims':        <SitePlaceDialog />,
    'drawing-setup':    <DrawingSetupDialog />,
    'revision-notes':   <RevisionNotesDialog />,
    'slope-calc':       <SlopeCalcDialog />,
    'materials-list':   <MaterialsListDialog />,
    'batch-utilities':  <BatchUtilitiesDialog />,
    'export-panel-dxf': <ExportPanelDXF />,
    'export-site-dxf':  <ExportSiteDXF />,
    'panel-book-pdf':   <PanelBookDialog />,
    'import-dxf':       <ImportDXFDialog />,
    'export-cvp':       <ExportCVPDialog />,
    'export-cvt':       <ExportCVTDialog />,
    'export-cvpanel':   <ExportCVPanelDialog />,
    'export-cvsite':    <ExportCVSiteDialog />,
    'import-cv':        <ImportCVDialog />,
    'about':            <AboutDialog />,
  };

  const content = map[dialog] ?? null;
  if (!content) return null;

  return (
    <div className="dialog-overlay" onClick={backdrop} role="dialog" aria-modal="true">
      {content}
    </div>
  );
}
